// Home.tsx — schermata principale (v019, nuova grafica): il mio avatar in grande, il marchio, «Sfida un amico», i
// riquadri Store / Torneo / Partita a 4 e, sotto una riga, «Come si gioca». Niente informazioni tecniche: se la rete
// non va lo dice la fascia di AvvisoRete. «Prova da solo» è dentro «Come si gioca».
import { DomandaNotifiche } from "../componenti/Notifiche.tsx";
import { daFesteggiare, segnaFesteggiato, serieAttuale } from "../servizi/giorniSalvati.ts";
import { coriandoli } from "../gioco/festa.ts";
import { leggiRegoleStelle } from "../servizi/social.ts";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Finestra } from "../componenti/Finestra.tsx";
import { ScegliLingua } from "../componenti/ScegliLingua.tsx";
import { InterruttoreTestoGrande } from "../componenti/TestoGrande.tsx";
import { Marchio } from "../componenti/Marchio.tsx";
import { Avatar } from "../componenti/Avatar.tsx";
import { useFacciaCasuale } from "../componenti/Faccina.tsx";
import { CreaSfida } from "../gioco/CreaSfida.tsx";
import { CreaTorneo } from "../torneo/CreaTorneo.tsx";
import { CreaGruppo } from "../gruppo/CreaGruppo.tsx";
import { useAccount, useAvatar } from "../servizi/account.ts";
import { useRicevute } from "../servizi/amici.ts";
import { vai } from "../servizi/percorso.ts";
import { eBooleano, leggiImpostazione } from "../servizi/impostazioni.ts";
import { nomeNativo } from "../i18n/index.ts";
import type { ControlloTema } from "../temi/useTema.ts";
import type { StatoImpostazioni } from "../servizi/impostazioni.ts";
import { Tutorial, type TipoTutorial } from "../componenti/Tutorial.tsx";
import { PubblicitaPrima, servePubblicitaPrima } from "../gioco/PubblicitaPrima.tsx";
import { Benvenuto, GuidaLuci, segnaGuida, serveGuida } from "../componenti/Guida.tsx";
import { voltoAccettato } from "../servizi/account.ts";
import { scelteFatte } from "../servizi/scelte.ts";
import { InvitaRubrica } from "../componenti/InvitaRubrica.tsx";
import { rubricaDisponibile } from "../servizi/contatti.ts";
import { apriPrivacyAdMob, privacyAdMobRichiesta } from "../servizi/admob.ts";
import { indirizzoSito } from "../gioco/Condividi.tsx";
import type { StatoTempoReale } from "../servizi/tempoReale.ts";
import {
  BASE_API,
  IMP_APP,
  PERCORSO_ACCESSO,
  PERCORSO_AIUTO,
  PERCORSO_AMICI,
  PERCORSO_DISPONIBILI,
  PERCORSO_AVATAR,
  PERCORSO_CLASSIFICA,
  PERCORSO_NEGOZIO,
  PERCORSO_PRIVACY,
  PERCORSO_PROFILO,
  PERCORSO_PROVA,
  PERCORSO_REGOLE,
  PERCORSO_TERMINI,
  VERSIONE_APP,
} from "../costanti.ts";

interface Props {
  tema: ControlloTema;
  temiDisponibili: readonly string[];
  temiUsabili: readonly string[];
  lingue: readonly string[];
  lingua: string;
  onLingua: (l: string) => void;
  server: StatoImpostazioni;
  tempoReale: StatoTempoReale;
  giocoAttivo: boolean;
  /** Apre subito la finestra «Sfida un amico» (es. «Nuova sfida» a fine partita). */
  apriSfida?: boolean;
}

function Stella({ classe }: { classe: string }) {
  return (
    <svg className={`ld-stella ${classe}`} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2l2.6 6.3 6.8.5-5.2 4.4 1.6 6.6L12 16.3 6.2 19.8l1.6-6.6L2.6 8.8l6.8-.5Z" />
    </svg>
  );
}

/** Moneta dei crediti (stella nella moneta d'oro). */
export function Moneta() {
  return (
    <svg className="ld-moneta" viewBox="0 0 30 30" aria-hidden="true">
      <circle cx="15" cy="15" r="13" fill="#F5B400" />
      <circle cx="15" cy="15" r="10" fill="#FFD60A" />
      <path d="M15 8.5l1.9 4 4.4.4-3.3 2.9 1 4.3L15 17.8l-4 2.3 1-4.3-3.3-2.9 4.4-.4Z" fill="#B37800" />
    </svg>
  );
}

function Riquadro({ titolo, sotto, etichetta, icona, onClick, guida }: { titolo: string; sotto?: string; etichetta?: "silver" | "gold"; icona: ReactNode; onClick: () => void; guida?: string }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="ld-riquadro" onClick={onClick} data-guida={guida}>
      {etichetta && <span className={`ld-etichetta ld-etichetta-${etichetta}`}>{t(`piani.nome_${etichetta}`)}</span>}
      {icona}
      <span className="ld-riquadro-titolo">{titolo}</span>
      {sotto && <span className="ld-riquadro-sotto">{sotto}</span>}
    </button>
  );
}

export function Home(p: Props) {
  const { t } = useTranslation();
  const [lingue, setLingue] = useState(false);
  const [sceltaTutorial, setSceltaTutorial] = useState(false);
  const [tutorialAperto, setTutorialAperto] = useState<TipoTutorial | null>(null);
  const [sfida, setSfida] = useState(p.apriSfida === true);
  // v045: per chi è in Base, ogni tanto un annuncio prima di giocare
  const [prima, setPrima] = useState(false);
  const [torneo, setTorneo] = useState(false);
  const [gruppo, setGruppo] = useState(false);
  // v045: la prima volta, il benvenuto della mascotte e poi la guida con le luci (chi ha già giocato non la vede)
  const [guida, setGuida] = useState<"benvenuto" | "luci" | null>(() => {
    if (!serveGuida("benv")) return null;
    if (voltoAccettato() || scelteFatte()) {
      segnaGuida("benv");
      segnaGuida("home");
      return null;
    }
    return "benvenuto";
  });
  const torneoAttivo = leggiImpostazione(IMP_APP.torneoAttivo, true, eBooleano) && p.giocoAttivo;
  const account = useAccount();
  const avatar = useAvatar();
  const facciaHome = useFacciaCasuale();
  const serie = useMemo(() => serieAttuale(), []);
  const [premioGiorno, setPremioGiorno] = useState(0);
  // v040: la partita del giorno appena fatta si festeggia una volta (riquadro d'oro e coriandoli), poi sparisce
  const [festa, setFesta] = useState(() => daFesteggiare());
  const [serveChiunque, setServeChiunque] = useState(false);
  const telaFesta = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!festa) return;
    segnaFesteggiato();
    const tela = telaFesta.current;
    const ferma = tela ? coriandoli(tela, ["#ffd23f", "#ff8a00", "#ffffff", "#5cf2b0"], 2200) : () => undefined;
    return ferma;
  }, [festa]);
  const registratoHome = account.tipo === "registrato";
  useEffect(() => {
    if (registratoHome) void leggiRegoleStelle().then((r) => setPremioGiorno(r.attivi ? (r.giorno ?? 0) : 0)).catch(() => undefined);
  }, [registratoHome]);
  // Partita a 4 (Gold): si vede se è accesa e se Gold si può comprare (o lo si ha già).
  const gruppiVisibili =
    p.giocoAttivo &&
    leggiImpostazione(IMP_APP.gruppiAttivi, true, eBooleano) &&
    (leggiImpostazione(IMP_APP.goldInVendita, false, eBooleano) || (account.tipo === "registrato" && account.io.piano === "gold"));
  const accountAttivi = leggiImpostazione(IMP_APP.accountAttivi, true, eBooleano);
  const registrato = account.tipo === "registrato";
  const amiciVisibili = accountAttivi && p.giocoAttivo && leggiImpostazione(IMP_APP.amiciAttivi, true, eBooleano);
  const ricevute = useRicevute();

  const riquadri: ReactNode[] = [];
  if (accountAttivi)
    riquadri.push(
      <Riquadro
        key="store"
        guida="store"
        titolo={t("store.titolo")}
        onClick={() => vai(PERCORSO_NEGOZIO)}
        icona={
          <svg className="ld-icona ld-icona-ciano" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 8h14l-1 12H6Z" />
            <path d="M9 8V6a3 3 0 0 1 6 0v2" />
            <path className="ld-pieno" d="M12 12.5l.9 1.8 2 .2-1.5 1.3.5 2-1.9-1.1-1.9 1.1.5-2-1.5-1.3 2-.2Z" />
          </svg>
        }
      />,
    );
  // v034: il Wall of Fame è un riquadro come gli altri (prima era un link piccolo in fondo alla pagina)
  if (leggiImpostazione(IMP_APP.classificaAttiva, true, eBooleano))
    riquadri.push(
      <Riquadro
        key="wall"
        guida="wall"
        titolo={t("social.wall_of_fame")}
        sotto={t("home.wall_sotto")}
        onClick={() => vai(PERCORSO_CLASSIFICA)}
        icona={
          <svg className="ld-icona ld-icona-oro" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 8l4 3 4-6 4 6 4-3-2 10H6Z" />
            <path className="ld-pieno" d="M12 12.2l.8 1.6 1.8.2-1.3 1.2.4 1.8-1.7-1-1.7 1 .4-1.8-1.3-1.2 1.8-.2Z" />
          </svg>
        }
      />,
    );
  if (torneoAttivo)
    riquadri.push(
      <Riquadro
        key="torneo"
        titolo={t("home.torneo")}
        sotto={t("home.torneo_sotto")}
        etichetta="silver"
        onClick={() => setTorneo(true)}
        icona={
          <svg className="ld-icona ld-icona-giallo" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 4h10v5a5 5 0 0 1-10 0Z" />
            <path d="M7 6H4c0 3 1.5 5 4 5M17 6h3c0 3-1.5 5-4 5M12 14v4M8 20h8" />
          </svg>
        }
      />,
    );
  if (gruppiVisibili)
    riquadri.push(
      <Riquadro
        key="gruppo"
        titolo={t("gruppo.organizza")}
        sotto={t("home.gruppo_sotto")}
        etichetta="gold"
        onClick={() => setGruppo(true)}
        icona={
          <svg className="ld-icona ld-icona-rosa" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="8" cy="8" r="3" />
            <circle cx="16" cy="8" r="3" />
            <path d="M2.5 19c.5-3 2.7-5 5.5-5s5 2 5.5 5M10.5 19c.5-3 2.7-5 5.5-5s5 2 5.5 5" />
          </svg>
        }
      />,
    );

  return (
    <div className="pagina ld-home">
      <div className="ld-alone" aria-hidden="true" />
      <header className="ld-barra">
        {accountAttivi ? (
          <button
            type="button"
            className="ld-profilo"
            data-guida="profilo"
            aria-label={registrato ? t("account.profilo") : t("account.accedi")}
            disabled={account.tipo === "caricamento"}
            onClick={() => vai(registrato ? PERCORSO_PROFILO : PERCORSO_ACCESSO)}
          >
            {registrato ? (
              <Avatar quale={avatar} dimensione={46} />
            ) : (
              <span className="ld-profilo-vuoto">
                <svg viewBox="0 0 40 40" aria-hidden="true">
                  <circle cx="20" cy="15" r="8" />
                  <path d="M5 40c1.5-9 7.5-14 15-14s13.5 5 15 14Z" />
                </svg>
              </span>
            )}
            {!registrato && (
              <span className="ld-profilo-piu" aria-hidden="true">
                +
              </span>
            )}
          </button>
        ) : (
          <span />
        )}
        {accountAttivi && (
          <button
            type="button"
            className="ld-chip-crediti"
            aria-label={registrato ? t("store.chip", { n: account.io.crediti }) : t("store.titolo")}
            onClick={() => vai(PERCORSO_NEGOZIO)}
          >
            <Moneta />
            <span>{registrato ? account.io.crediti : t("store.titolo")}</span>
          </button>
        )}
      </header>

      <main className="ld-home-centro">
        <button type="button" className="ld-eroe" aria-label={t("avatar.cambia")} onClick={() => vai(PERCORSO_AVATAR)}>
          <Stella classe="ld-s1" />
          <Stella classe="ld-s2" />
          <Stella classe="ld-s3" />
          {/* v035b: la faccia grande cambia a ogni apertura (la mia resta in alto a sinistra) */}
          <Avatar quale={facciaHome} dimensione={232} classe="ld-eroe-avatar" />
        </button>
        <Marchio />
        <p className="ld-slogan">{t("home.slogan")}</p>
        {/* v037: giorni di fila e partita del giorno. v040: fatta oggi → il riquadro diventa d'oro, coriandoli, poi via */}
        {festa ? (
          <p className="ld-serie ld-serie-festa" role="status" onAnimationEnd={(e) => e.animationName === "ld-serie-via" && setFesta(false)}>
            <canvas ref={telaFesta} className="ld-serie-coriandoli" aria-hidden="true" />
            ⭐ {premioGiorno > 0 ? t("home.giorno_fatto", { n: premioGiorno }) : t("home.giorno_fatto_senza")}
          </p>
        ) : serie.giorni >= 2 ? (
          <p className="ld-serie" data-oggi={serie.oggi}>
            🔥 {t("home.serie", { n: serie.giorni })}
            {!serie.oggi && <span> · {t("home.serie_oggi")}</span>}
          </p>
        ) : (
          premioGiorno > 0 && <p className="ld-serie">⭐ {t("home.partita_giorno", { n: premioGiorno })}</p>
        )}
      </main>

      <section className="ld-home-azioni">
        <button
          type="button"
          className="ld-cta"
          disabled={!p.giocoAttivo}
          aria-describedby={p.giocoAttivo ? undefined : "gioco-spento"}
          data-guida="sfida"
          onClick={() => (servePubblicitaPrima() ? setPrima(true) : setSfida(true))}
        >
          <span>{t("home.sfida")}</span>
          <span className="ld-cta-freccia" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        </button>
        {/* v033: sotto «Sfida un amico»: «Sfida uno sconosciuto» (chi è online e accetta sfide da chiunque) e gli amici */}
        {amiciVisibili && (
          <div className="ld-home-social">
            <button
              type="button"
              className="ld-home-social-voce"
              onClick={() => {
                if (!registrato) return vai(PERCORSO_ACCESSO);
                // v041: per sfidare persone nuove bisogna poter essere sfidati da chiunque: lo si dice subito
                const io = account.tipo === "registrato" ? account.io : null;
                if (io?.puoLink && !io.apertoATutti) return setServeChiunque(true);
                vai(PERCORSO_DISPONIBILI);
              }}
            >
              <svg className="ld-icona ld-icona-ciano" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" />
              </svg>
              <span>{t("home.sconosciuto")}</span>
            </button>
            <button type="button" className="ld-home-social-voce" onClick={() => vai(registrato ? PERCORSO_AMICI : PERCORSO_ACCESSO)}>
              <svg className="ld-icona ld-icona-rosa" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="9" cy="8" r="3.2" />
                <path d="M3 19c.6-3.3 3-5.2 6-5.2s5.4 1.9 6 5.2" />
                <path d="M16 5.2a3 3 0 0 1 0 5.6M18 13.9c1.6.7 2.7 2.4 3 5.1" />
              </svg>
              <span>{t("amici.titolo")}</span>
              {ricevute > 0 && (
                <span className="ld-bollino" aria-label={t("amici.ricevute", { n: ricevute })}>
                  {ricevute}
                </span>
              )}
            </button>
          </div>
        )}
        {/* v046: invitare le persone della rubrica anche senza aprire una sfida (il link porta a sfidare me) */}
        {p.giocoAttivo && rubricaDisponibile() && (
          <InvitaRubrica
            compatto
            link={registrato && account.io.nomeUtente && account.io.linkAttivo ? `${indirizzoSito()}/@${account.io.nomeUtente}` : indirizzoSito()}
            testo={t("fine.amico_testo")}
          />
        )}
        {!p.giocoAttivo && (
          <p id="gioco-spento" className="nota-centrata">
            {t("gioco.errore_gioco_spento")}
          </p>
        )}
        {riquadri.length > 0 && (
          <div className="ld-riquadri" data-numero={riquadri.length}>
            {riquadri}
          </div>
        )}
        {registrato && account.io.tester && (
          <button type="button" className="pulsante pulsante-piccolo" onClick={() => vai(PERCORSO_PROVA)}>
            {t("home.strumenti_prova")}
          </button>
        )}
      </section>
      {/* v038: le notifiche, chieste una volta a chi ha l'account */}
      {registrato && <DomandaNotifiche />}

      <footer className="ld-piede">
        <div className="ld-piede-riga">
          <button type="button" className="ld-come" data-guida="come" onClick={() => vai(PERCORSO_AIUTO)}>
            <span className="ld-come-cerchio" aria-hidden="true">
              ?
            </span>
            {t("home.come_si_gioca")}
          </button>
          {/* v030: il mini tutorial della partita, da rivedere quando si vuole */}
          <button type="button" className="ld-wall ld-tutorial-apri" onClick={() => setSceltaTutorial(true)}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="6" y="2.5" width="12" height="19" rx="3" />
              <path d="M10 18.5h4M9 7.5l6 3.5-6 3.5z" />
            </svg>
            <span>{t("home.tutorial")}</span>
          </button>
          {p.lingue.length > 1 && (
            <button type="button" className="ld-lingua" aria-label={t("lingua.titolo")} onClick={() => setLingue(true)}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" />
              </svg>
              <span>{nomeNativo(p.lingua)}</span>
            </button>
          )}
        </div>
        <nav className="ld-legale">
          <a href={`${BASE_API}${PERCORSO_PRIVACY}?lingua=${encodeURIComponent(p.lingua)}`} target="_blank" rel="noopener">
            {t("legale.privacy")}
          </a>
          <a href={`${BASE_API}${PERCORSO_TERMINI}?lingua=${encodeURIComponent(p.lingua)}`} target="_blank" rel="noopener">
            {t("legale.termini")}
          </a>
          <a href={`${BASE_API}${PERCORSO_REGOLE}?lingua=${encodeURIComponent(p.lingua)}`} target="_blank" rel="noopener">
            {t("legale.regole")}
          </a>
          {/* v046: nelle app, le scelte sulla pubblicità (consenso richiesto in Europa per AdMob) */}
          {privacyAdMobRichiesta() && (
            <button type="button" className="pulsante-link" onClick={() => void apriPrivacyAdMob()}>
              {t("legale.privacy_pubblicita")}
            </button>
          )}
          <span>{t("home.versione", { versione: VERSIONE_APP })}</span>
        </nav>
      </footer>

      {/* v041: «Sfida uno sconosciuto» con «Chi può sfidarti» su Nessuno o Link: si spiega e si porta all'opzione giusta */}
      <Finestra aperta={serveChiunque} titolo={t("disponibili.serve_chiunque_titolo")} onChiudi={() => setServeChiunque(false)}>
        <p>{t("disponibili.serve_chiunque_testo")}</p>
        <div className="azioni-riga">
          <button type="button" className="pulsante pulsante-secondario" onClick={() => setServeChiunque(false)}>
            {t("generale.annulla")}
          </button>
          <button
            type="button"
            className="pulsante pulsante-primario"
            onClick={() => {
              setServeChiunque(false);
              vai(`${PERCORSO_PROFILO}?evidenzia=chiunque`);
            }}
          >
            {t("disponibili.serve_chiunque_cambia")}
          </button>
        </div>
      </Finestra>
      <Finestra
        aperta={lingue}
        titolo={t("lingua.elenco")}
        icona={
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" />
          </svg>
        }
        onChiudi={() => setLingue(false)}
      >
        <ScegliLingua
          lingue={p.lingue}
          lingua={p.lingua}
          onLingua={(l) => {
            p.onLingua(l);
            setLingue(false);
          }}
        />
        <InterruttoreTestoGrande />
      </Finestra>

      <Finestra aperta={sceltaTutorial} titolo={t("home.tutorial")} onChiudi={() => setSceltaTutorial(false)}>
        <p className="nota">{t("tutorial.scegli")}</p>
        <div className="ld-tutorial-scelta">
          {(["1v1", "torneo", "gruppo"] as const).map((k) => (
            <button
              key={k}
              type="button"
              className="ld-lingue-voce"
              onClick={() => {
                setSceltaTutorial(false);
                setTutorialAperto(k);
              }}
            >
              {t(`tutorial.tipo_${k}`)}
            </button>
          ))}
        </div>
      </Finestra>
      {tutorialAperto && <Tutorial tipo={tutorialAperto} onFine={() => setTutorialAperto(null)} />}

      <Finestra aperta={sfida} titolo={t("home.sfida")} onChiudi={() => setSfida(false)}>
        {sfida && <CreaSfida onAnnulla={() => setSfida(false)} />}
      </Finestra>
      {prima && (
        <PubblicitaPrima
          onFine={() => {
            setPrima(false);
            setSfida(true);
          }}
        />
      )}
      {guida === "benvenuto" && <Benvenuto onGuida={() => setGuida("luci")} onChiudi={() => setGuida(null)} />}
      {guida === "luci" && (
        <GuidaLuci
          chiave="home"
          passi={[
            { sel: '[data-guida="sfida"]', testo: t("guida.home_sfida") },
            { sel: '[data-guida="store"]', testo: t("guida.home_store") },
            { sel: '[data-guida="wall"]', testo: t("guida.home_wall") },
            { sel: '[data-guida="profilo"]', testo: t("guida.home_profilo") },
            { sel: '[data-guida="come"]', testo: t("guida.home_come") },
            { sel: '[data-guida="sfida"]', testo: t("guida.home_via") },
          ]}
          onFine={() => setGuida(null)}
        />
      )}

      <Finestra aperta={torneo} titolo={t("torneo.organizza")} onChiudi={() => setTorneo(false)}>
        {torneo && <CreaTorneo onAnnulla={() => setTorneo(false)} />}
      </Finestra>

      <Finestra aperta={gruppo} titolo={t("gruppo.organizza")} onChiudi={() => setGruppo(false)}>
        {gruppo && <CreaGruppo onAnnulla={() => setGruppo(false)} />}
      </Finestra>
    </div>
  );
}
